import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Importar traducciones
import esCommon from '@/data/locales/es/common.json';
import enCommon from '@/data/locales/en/common.json';
import ptCommon from '@/data/locales/pt/common.json';

// Lotes de traducción (datos puros) mezclados en el namespace `common` bajo su
// prefijo: t('ventas.x'), t('clHeader.x'), t('secServices.x'), etc.
import * as clBatch1 from '@/i18n/auto/clBatch1';
import * as clBatch2 from '@/i18n/auto/clBatch2';
import * as clBatch3 from '@/i18n/auto/clBatch3';
import * as clBatch4 from '@/i18n/auto/clBatch4';
import * as clBatch5 from '@/i18n/auto/clBatch5';
import * as clBatch6 from '@/i18n/auto/clBatch6';
import * as clBatch7 from '@/i18n/auto/clBatch7';

const resources = {
  es: {
    common: { ...esCommon, ...clBatch1.es, ...clBatch2.es, ...clBatch3.es, ...clBatch4.es, ...clBatch5.es, ...clBatch6.es, ...clBatch7.es },
  },
  en: {
    common: { ...enCommon, ...clBatch1.en, ...clBatch2.en, ...clBatch3.en, ...clBatch4.en, ...clBatch5.en, ...clBatch6.en, ...clBatch7.en },
  },
  pt: {
    common: ptCommon,
  },
};

// Configuración de detección de idioma
const detectionOptions = {
  order: ['localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
  lookupLocalStorage: 'preferredLanguage',
  caches: ['localStorage'],
  excludeCacheFor: ['cimode'],
  checkWhitelist: true
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'es',
    // Idiomas realmente soportados (LC-36). Evita que un tenant que declare un idioma
    // sin diccionario (p. ej. 'eu') haga fallback silencioso: i18next solo usará estos.
    // `pt` se mantiene (LC-38): está en paridad con es/en y disponible si algún tenant lo usa.
    supportedLngs: ['es', 'en', 'pt'],
    nonExplicitSupportedLngs: true,
    defaultNS: 'common',
    
    detection: detectionOptions,

    interpolation: {
      escapeValue: false,
    },

    // Configuración de desarrollo
    debug: import.meta.env.DEV,
    
    // Configuración de carga
    load: 'languageOnly',
    preload: ['es', 'en', 'pt'],
    
    // Configuración de namespace
    ns: ['common'],
    
    // Configuración de plurales
    pluralSeparator: '_',
    contextSeparator: '_',
    
    // Configuración de reactividad
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
    },
  });

export default i18n;

// Función para agregar nuevos idiomas dinámicamente
export const addLanguageResources = (language: string, namespace: string, resources: any) => {
  i18n.addResourceBundle(language, namespace, resources, true, true);
};

// Función para verificar si un idioma está cargado
export const isLanguageLoaded = (language: string) => {
  return i18n.hasResourceBundle(language, 'common');
};

// Función para cargar idiomas bajo demanda
export const loadLanguage = async (language: string) => {
  if (!isLanguageLoaded(language)) {
    try {
      const resources = await import(`@/data/locales/${language}/common.json`);
      addLanguageResources(language, 'common', resources.default);
      return true;
    } catch (error) {
      console.warn(`Failed to load language ${language}:`, error);
      return false;
    }
  }
  return true;
};